import type { DailyLog } from './types';

interface DailyLogCardProps {
  log: DailyLog;
  onEdit: (log: DailyLog) => void;
  onDelete: (logId: string) => void;
  onToggleShare?: (log: DailyLog) => void;
}

export const DailyLogCard = ({ log, onEdit, onDelete, onToggleShare }: DailyLogCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getWeatherIcon = (weather: string) => {
    switch (weather.toLowerCase()) {
      case 'clear':
        return 'wb_sunny';
      case 'partly cloudy':
      case 'cloudy':
        return 'wb_cloudy';
      case 'rain':
      case 'heavy rain':
        return 'rainy';
      case 'snow':
        return 'ac_unit';
      case 'fog':
        return 'foggy';
      case 'windy':
        return 'air';
      default:
        return 'wb_cloudy';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header Row */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            {formatDate(log.log_date)}
          </h3>
          <p className="text-sm text-gray-500">
            Logged by {log.created_by_name || 'Unknown'} · {new Date(log.created_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {onToggleShare && (
            <button
              onClick={() => onToggleShare(log)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                log.visible_to_client
                  ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
              title={log.visible_to_client ? 'Client can see this log in their portal — click to unshare' : 'Share this log to the client portal'}
            >
              <span className="material-symbols-outlined text-base">
                {log.visible_to_client ? 'visibility' : 'visibility_off'}
              </span>
              {log.visible_to_client ? 'Shared with client' : 'Share with client'}
            </button>
          )}
          <button
            onClick={() => onEdit(log)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Edit log"
          >
            <span className="material-symbols-outlined text-xl">edit</span>
          </button>
          <button
            onClick={() => onDelete(log.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete log"
          >
            <span className="material-symbols-outlined text-xl">delete</span>
          </button>
        </div>
      </div>

      {/* Weather and Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-yellow-500">
            {getWeatherIcon(log.weather)}
          </span>
          <div>
            <p className="text-xs text-gray-500">Weather</p>
            <p className="text-sm font-medium text-gray-900">
              {log.weather} • {log.temperature}°C
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">group</span>
          <div>
            <p className="text-xs text-gray-500">Crew Size</p>
            <p className="text-sm font-medium text-gray-900">
              {log.crew_size} {log.crew_size === 1 ? 'person' : 'people'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-green-500">schedule</span>
          <div>
            <p className="text-xs text-gray-500">Hours Worked</p>
            <p className="text-sm font-medium text-gray-900">
              {log.hours_worked} hrs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-blue-500">calculate</span>
          <div>
            <p className="text-xs text-gray-500">Total Hours</p>
            <p className="text-sm font-medium text-gray-900">
              {log.crew_size * log.hours_worked} hrs
            </p>
          </div>
        </div>
      </div>

      {/* Work Performed */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <span className="material-symbols-outlined text-base">construction</span>
          Work Performed
        </h4>
        <p className="text-sm text-gray-900 whitespace-pre-wrap">
          {log.work_performed}
        </p>
      </div>

      {/* Materials Used */}
      {log.materials_used && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">inventory_2</span>
            Materials Used
          </h4>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {log.materials_used}
          </p>
        </div>
      )}

      {/* Equipment Used */}
      {log.equipment_used && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">precision_manufacturing</span>
            Equipment Used
          </h4>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {log.equipment_used}
          </p>
        </div>
      )}

      {/* Visitors */}
      {log.visitors && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">person_pin_circle</span>
            Visitors
          </h4>
          <p className="text-sm text-gray-900">
            {log.visitors}
          </p>
        </div>
      )}

      {/* Safety Incidents */}
      {log.safety_incidents && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">warning</span>
            Safety Incidents
          </h4>
          <p className="text-sm text-red-900 whitespace-pre-wrap">
            {log.safety_incidents}
          </p>
        </div>
      )}

      {/* Additional Notes */}
      {log.notes && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">note</span>
            Additional Notes
          </h4>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {log.notes}
          </p>
        </div>
      )}
    </div>
  );
};


